import {clickOutside} from '@malt/dom';
import {escapeHtml} from '@malt/sanitize';

export class TooltipController {
    constructor(root, params = {}) {
        this._root = root;
        this._params = params;

        this._toggleTooltip = this._toggleTooltip.bind(this);
        this._onclickOutside = this._onclickOutside.bind(this);
        this._createTooltip = this._createTooltip.bind(this);
        this._onMouseLeave = this._onMouseLeave.bind(this);
    }

    _getTooltipContent() {
        // In specific cases, for example in handlebars templates, where it can tricky to give a full text as parameter (conflicts with single quotes),
        // you can use a data-tooltip-content attribute
        const dataTooltip = this._root.querySelector('[data-tooltip-content]');
        if (dataTooltip) {
            const content = dataTooltip.cloneNode(true);
            content.removeAttribute('hidden');
            return content.outerHTML;
        }

        if (this._params.unsafeNoHtmlEscape) {
            return this._params.content;
        }

        return escapeHtml(this._params.content);
    }

    async _targetTriggerElement() {
        const trigger = this._root.classList.contains('js-trigger') ? this._root : this._root.querySelector('.js-trigger');

        // Temp fix: when the module is init, js-trigger is not found because component is loading
        // We use componentOnReady hook to wait for it
        if (!trigger) {
            const joyIcon = await this._root.querySelector('joy-icon');
            if (joyIcon) {
                await customElements.whenDefined('joy-icon');
                await joyIcon.componentOnReady();
                return joyIcon;
            }
        }

        return trigger;
    }

    async _triggerIsAnIcon() {
        const triggerElement = await this._targetTriggerElement();
        return triggerElement.tagName === 'I';
    }

    _getRandomInt() {
        return Math.floor(Math.random() * Math.floor(999));
    }

    _isTooltipDefined() {
        return document.getElementById(this._tooltipId);
    }

    _getTooltip() {
        return document.getElementById(this._tooltipId).parentElement;
    }

    _addClass(el) {
        this._tooltipId = `tooltipId-${this._getRandomInt()}`;
        const idAlreadyExists = document.getElementById(this._tooltipId);

        if (el.getAttribute('id') === null && !idAlreadyExists) {
            el.setAttribute('id', this._tooltipId);
        } else {
            this._tooltipId = `tooltipId-${this._getRandomInt()}`;
            el.setAttribute('id', this._tooltipId);
        }

        if (this._params.customClass) {
            el.classList.add(this._params.customClass);
        }
        el.classList.add('c-tooltip_wrapper', 'js-bodyRoot');

        if (this._params.position) {
            el.classList.add(this._params.position);
        }
        if (this._params.color) {
            el.classList.add(this._params.color);
        }

        return el;
    }

    async _createTooltip(e) {
        const tooltipStyleWrapper = document.createElement('div');
        tooltipStyleWrapper.classList.add('joy-wrapper');

        const tooltipWrapper = document.createElement('div');

        const tooltipText = document.createElement('p');
        tooltipText.innerHTML = this._getTooltipContent();

        tooltipWrapper.appendChild(tooltipText);
        tooltipStyleWrapper.appendChild(tooltipWrapper);

        this._addClass(tooltipWrapper);

        const targetTopPosition = e.target.getBoundingClientRect().top + e.target.getBoundingClientRect().height;
        const targetToTop = window.pageYOffset + targetTopPosition;

        tooltipWrapper.style.top = this._params.insertAfterTrigger ? `${e.target.getBoundingClientRect().height}px` : `${targetToTop}px`;

        if (this._params.position === 'right') {
            const targetRightPosition = e.target.getBoundingClientRect().left + e.target.getBoundingClientRect().width;
            let targetToRight = document.body.clientWidth - targetRightPosition;
            if (await this._triggerIsAnIcon()) {
                // The idea is to detect "small" triggers like icons, to prevent tooltip arrows to be shifted. So far we only check if tagName is a <i>
                // Not perfect way to fix the issue, but for now, it works for this case
                targetToRight -= 15;
            }

            tooltipWrapper.style.right = this._params.insertAfterTrigger ? '0' : `${targetToRight}px`;
        } else {
            let targetToLeft = e.target.getBoundingClientRect().left;
            if (await this._triggerIsAnIcon()) {
                targetToLeft -= 15;
            }

            tooltipWrapper.style.left = this._params.insertAfterTrigger ? '0' : `${targetToLeft}px`;
        }

        if (this._params.insertAfterTrigger) {
            this._root.appendChild(tooltipStyleWrapper);
        } else {
            document.body.appendChild(tooltipStyleWrapper);
        }
    }

    _destroyTooltip(element) {
        element.remove();
    }

    _onclickOutside(e) {
        if (clickOutside(e, this._root)) {
            if (this._isTooltipDefined()) {
                this._destroyTooltip(this._getTooltip());
            }
        }
    }

    _onMouseLeave() {
        if (this._isTooltipDefined()) {
            this._destroyTooltip(this._getTooltip());
        }
    }

    async _toggleTooltip(e) {
        if (this._isTooltipDefined()) {
            this._destroyTooltip(this._getTooltip());
        } else {
            await this._createTooltip(e);
        }
    }

    async initialize() {
        if (this._params.type === 'click') {
            this._root.addEventListener('click', this._toggleTooltip);
            document.addEventListener('click', this._onclickOutside);
        } else {
            const triggerElement = await this._targetTriggerElement();
            triggerElement.addEventListener('mouseenter', this._createTooltip);
            triggerElement.addEventListener('mouseleave', this._onMouseLeave);
        }
    }

    async destroy() {
        const triggerElement = await this._targetTriggerElement();
        triggerElement.removeEventListener('mouseenter', this._createTooltip);
        triggerElement.removeEventListener('mouseleave', this._onMouseLeave);
        this._root.removeEventListener('click', this._toggleTooltip);
        document.removeEventListener('click', this._onclickOutside);

        if (this._isTooltipDefined()) {
            this._destroyTooltip(this._getTooltip());
        }
    }
}
